import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVerifyEmail } from '@/features/auth';
import { Button } from '@/shared/ui/Button';
import { AuthLayout } from './ui/AuthLayout';
import { authMessageClassName } from './ui/authForm';

// VerifyEmail на новом стеке. Auto-trigger при mount: token+hash из URL →
// POST /user/verify через useVerifyEmail mutation. На success бек отдаёт
// auth_key, useVerifyEmail сохраняет его в cookies + переводит store в
// authenticated. После этого hard-navigate в кабинет (как Login после
// успешного логина — нужно чтобы legacy AuthContext перемонтировался и
// подхватил cookies).
//
// Прямого 1:1 референса у фронтендера нет (наша специфика email-активации).

export function VerifyEmail() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const hash = params.get('hash') ?? '';
  const verifyMutation = useVerifyEmail();
  const [state, setState] = useState<'verifying' | 'ok' | 'error'>(
    !token || !hash ? 'error' : 'verifying',
  );
  // В состоянии держим ПРИЧИНУ, а не готовый текст: t() внутри useState
  // вычислился бы один раз и остался на языке первого рендера. Серверное
  // сообщение — исключение, оно уже приходит текстом (бэк на русском —
  // локализация ответов API в последней фазе эпика).
  const [errorReason, setErrorReason] = useState<'missingParams' | 'failed' | 'network' | null>(
    !token || !hash ? 'missingParams' : null,
  );
  const [serverMessage, setServerMessage] = useState<string | null>(null);

  const errorText =
    serverMessage ??
    (errorReason === 'missingParams'
      ? t('verify.missingParams')
      : errorReason === 'network'
        ? t('common:networkError')
        : t('verify.failed'));

  useEffect(() => {
    if (!token || !hash) return;
    let cancelled = false;
    verifyMutation
      .mutateAsync({ token, hash })
      .then((outcome) => {
        if (cancelled) return;
        if (outcome.kind === 'ok') {
          setState('ok');
        } else {
          setState('error');
          setErrorReason('failed');
          setServerMessage(outcome.message ?? null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setState('error');
        setErrorReason('network');
      });
    return () => {
      cancelled = true;
    };
    // verifyMutation стабилен через лайфтайм — не указываем в deps. token+hash
    // достаются один раз при mount странички.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, hash]);

  const handleGoToCabinet = () => {
    navigate('/', { replace: true });
  };

  // Заголовок и вопрос со ссылкой — по состоянию; раскладка одна (AuthLayout).
  const header = {
    verifying: { title: t('verify.pendingTitle'), questionText: '' },
    ok: { title: t('verify.doneTitle'), questionText: '' },
    error: { title: t('verify.failedTitle'), questionText: t('rememberedPassword') },
  }[state];

  return (
    <AuthLayout
      title={header.title}
      questionText={header.questionText}
      linkTo="/login"
      linkText={t('signIn')}
    >
      {state === 'verifying' && (
        <>
          <p className={authMessageClassName()}>{t('verify.pending')}</p>
        </>
      )}

      {state === 'ok' && (
        <>
          <p className={authMessageClassName()}>{t('verify.done')}</p>
          <div className="mt-8 w-full">
            <Button
              type="button"
              className="min-h-[48px] w-full !px-0"
              onClick={handleGoToCabinet}
            >
              {t('verify.toCabinet')}
            </Button>
          </div>
        </>
      )}

      {state === 'error' && (
        <>
          <p className={authMessageClassName('error')}>{errorText}</p>
          <div className="mt-8 w-full">
            <Button
              type="button"
              className="min-h-[48px] w-full !px-0"
              onClick={() => navigate('/login', { replace: true })}
            >
              {t('back')}
            </Button>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
